import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  pickup_suburb: "",
  delivery_suburb: "",
  notes: "",
};

const inputCls =
  "bg-white/5 border-white/10 text-snow placeholder:text-mist/50 focus-visible:ring-frost focus-visible:border-frost rounded-none h-12";

const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      toast.success(`Quote request received — reference ${data.reference}`, {
        description: "Our dispatch team will call you within one business hour.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send your request", {
        description: "Please try again or call 1800 724 336.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-32 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost font-bold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-frost inline-block" />
              Get a quote
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase font-medium">
              Book the<br />cold run
            </h2>
            <p className="mt-8 max-w-md text-mist font-light leading-relaxed">
              Tell us what you're moving and where it's headed. A real dispatcher —
              not a bot — will come back with a firm price within one business hour.
            </p>
            <div className="mt-12 space-y-4 font-mono text-xs uppercase tracking-[0.2em] text-mist">
              <a href="tel:1800724336" data-testid="quote-phone" className="flex items-center gap-3 hover:text-frost transition-colors duration-300">
                <span className="text-frost">PH</span> 1800 724 336
              </a>
              <a href="mailto:operations@coldfast.com.au" data-testid="quote-email" className="flex items-center gap-3 hover:text-frost transition-colors duration-300">
                <span className="text-frost">EM</span> operations@coldfast.com.au
              </a>
              <p data-testid="quote-hours" className="flex items-center gap-3">
                <span className="text-frost">HR</span> Dispatch 24/7 — bookings 6am-6pm
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-7" delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-surface/60 backdrop-blur-xl border border-white/10 p-8 lg:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="q-name" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Name *</Label>
                <Input id="q-name" data-testid="quote-input-name" required value={form.name} onChange={set("name")} placeholder="Alex Mercer" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-company" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Company</Label>
                <Input id="q-company" data-testid="quote-input-company" value={form.company} onChange={set("company")} placeholder="Harbour Meats Co." className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-email" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Email *</Label>
                <Input id="q-email" data-testid="quote-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com.au" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-phone" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Phone *</Label>
                <Input id="q-phone" data-testid="quote-input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Service required *</Label>
                <Select required value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="quote-select-service" className={`${inputCls} w-full`}>
                    <SelectValue placeholder="Select a temperature service" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-snow">
                    <SelectItem data-testid="quote-service-chilled" value="chilled" className="focus:bg-white/10 focus:text-snow">Chilled courier — 0-4°C</SelectItem>
                    <SelectItem data-testid="quote-service-frozen" value="frozen" className="focus:bg-white/10 focus:text-snow">Frozen transport — -18°C</SelectItem>
                    <SelectItem data-testid="quote-service-deepfrozen" value="deep-frozen" className="focus:bg-white/10 focus:text-snow">Deep-frozen &amp; ice cream — -25°C</SelectItem>
                    <SelectItem data-testid="quote-service-pharma" value="pharma" className="focus:bg-white/10 focus:text-snow">Pharmaceutical — 2-8°C</SelectItem>
                    <SelectItem data-testid="quote-service-hospitality" value="hospitality" className="focus:bg-white/10 focus:text-snow">Hospitality & events — multi-drop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-pickup" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Pickup suburb *</Label>
                <Input id="q-pickup" data-testid="quote-input-pickup" required value={form.pickup_suburb} onChange={set("pickup_suburb")} placeholder="Eastern Creek" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-delivery" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Delivery suburb *</Label>
                <Input id="q-delivery" data-testid="quote-input-delivery" required value={form.delivery_suburb} onChange={set("delivery_suburb")} placeholder="Bondi Junction" className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="q-notes" className="font-mono text-xs uppercase tracking-[0.15em] text-mist">Freight details</Label>
                <Textarea
                  id="q-notes"
                  data-testid="quote-input-notes"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="e.g. 12 cartons of gelato, pickup Thursday 7am, tailgate needed at delivery"
                  className={`${inputCls} min-h-[120px] h-auto`}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-btn"
              className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-blaze text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-5 hover:bg-frost hover:text-abyss transition-colors duration-300 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending request
                </>
              ) : (
                <>
                  Request my quote
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                </>
              )}
            </button>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mist/60">
              No spam. No lock-ins. Just a firm price.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
